import { action, computed, observable, runInAction } from 'mobx'
import { $RootStore } from '../../../src/stores/RootStore'
import listeners from '../../communication/Listeners'
import { GetSalonQuery, MySalonMembershipsQuery, ArchivedSalonMembersQuery } from '../../../graphQL/generated/client'
import { SalonMemberOnboardingStep, SalonMemberPermission, SalonMemberStatus } from '../../../graphQL/generated/enums'
import { randomMemberColour } from '../../lib/categoryColours'
import { requestErrorCode } from '../../../shared-lib/communication/BaseErrorHandler'
import { SALON_MEMBER_HAS_UPCOMING_BOOKINGS } from '../../../shared-lib/communication/ErrorHelper'
import { GettingStartedStepIds } from '../SetupStore/stepData/gettingStarted'
import type { AddedWorkplace } from '../../lib/workplaces'

export type Salon = NonNullable<GetSalonQuery['salon']>
export type SalonMembership = NonNullable<MySalonMembershipsQuery['mySalonMemberships']>[number]
export type SalonMember = NonNullable<Salon['members']>[number]
export type SalonMemberOnboardingSteps = SalonMember['completedOnboardingSteps']

export const isSalonOwner = (membership: SalonMembership): boolean =>
  membership.permissions.includes(SalonMemberPermission.Owner)

export enum ArchiveMemberResult {
  Archived = 'archived',
  UpcomingBookings = 'upcomingBookings',
  Failed = 'failed',
}

// What salonMemberUpdated pushes: the membership document, without the professional-profile fields
// (businessName, locations) that the query joins on and that change by their own route.
export type SalonMembershipUpdate = Omit<SalonMembership, 'businessName' | 'salonEmailAddress' | 'locations' | 'colleagues' | 'onlinePaymentsEnabled'>
export type ArchivedSalonMember = ArchivedSalonMembersQuery['archivedSalonMembers'][number]
// Calendar display fields for a colleague.
export type SalonColleague = NonNullable<SalonMembership['colleagues']>[number]
export type MemberPickerMember = {
  id: string
  name: string
  color: string
  picture?: string | null
  isAvailableAtLocation?: boolean
}

export type TeamDayRow = {
  member: MemberPickerMember
  loaded: boolean
  bookings: number
  available: boolean
  working: boolean
  statusText: string
}

export type AccountOption = {
  id: string
  salonId: string | null
  label: string
  subtitles: string[]
  isActive: boolean
  nameLoading?: boolean
}

export const PERSONAL_OPTION_ID = 'personal'

export default class SalonStore {
  rootStore: $RootStore

  @observable accessor salon: Salon | null = null
  @observable accessor error: string | null = null
  @observable accessor mySalonMemberships: SalonMembership[] = []
  @observable accessor membershipsLoaded = false
  @observable accessor addedWorkplace: AddedWorkplace | null = null
  salonMemberSubscription?: { unsubscribe: () => void }

  constructor(rootStore: $RootStore) {
    this.rootStore = rootStore
  }

  // Derived from in-flight requests rather than a manually-toggled flag. NB: this query's
  // operation name is `getSalon` while its Query field is `salon`, so operationName isn't a typed
  // key here (every other query names its operation after its field) — hence the string compare.
  @computed get loading(): boolean {
    return this.rootStore.uiStore.activeRequests.some(r => (r.operationName as string) === 'getSalon')
  }

  @computed get members() {
    return this.salon?.members ?? []
  }

  // Everyone who actually works at the salon, and can therefore be booked, scheduled or priced.
  // SERVICE_PROVIDER is what marks that: an admin who only manages the salon isn't one, and the owner
  // is included only once they give themselves SERVICE_PROVIDER too.
  // Invitees are on the roster (the team screen lists them as pending) but have no membership the
  // API will act on — it resolves availability owners and booking allocations from active
  // memberships only — so they stay out of agendas, availability and allocation until they accept.
  @computed get workingMembers(): SalonMember[] {
    return this.members.filter(m =>
      m.status === SalonMemberStatus.Active
      && m.permissions.includes(SalonMemberPermission.ServiceProvider))
  }

  @computed get workingAndInvitedMembers(): SalonMember[] {
    return this.members.filter(m =>
      (m.status === SalonMemberStatus.Active || m.status === SalonMemberStatus.Invited)
      && m.permissions.includes(SalonMemberPermission.ServiceProvider))
  }

  @computed get hasInvitedTeam(): boolean {
    return this.members.some(m =>
      m.status !== SalonMemberStatus.Archived
      && !m.permissions.includes(SalonMemberPermission.Owner)
      && !m.permissions.includes(SalonMemberPermission.Receptionist))
  }

  // The professionals a booking can be allocated to.
  @computed get professionalOptions(): MemberPickerMember[] {
    return this.workingMembers.map(m => ({
      id: m.memberId,
      name: m.displayName,
      color: m.color,
      picture: m.picture,
    }))
  }

  // Looks up a roster row by user id (`SalonMember.memberId`), not by membership document `_id`.
  findMember(memberId: string): SalonMember | undefined {
    return this.members.find(m => m.memberId === memberId)
  }

  // Colleagues from every salon that grants us their calendars; re-checked so a revoke hides them at once.
  @computed get colleagues(): SalonColleague[] {
    return this.mySalonMemberships
      .filter(m => m.permissions.includes(SalonMemberPermission.ViewOtherMemberCalendars))
      .flatMap(m => m.colleagues ?? [])
  }

  // Someone who shares two salons with us is listed under both.
  @computed get colleagueOptions(): MemberPickerMember[] {
    const unique = [...new Map(this.colleagues.map(c => [c.memberId, c])).values()]

    return unique.map(c => ({
      id: c.memberId,
      name: c.displayName,
      color: c.color,
      picture: c.picture,
    }))
  }

  // Use the salon roster for salon sessions and cached colleagues for members.
  @computed get viewableCalendarOptions(): MemberPickerMember[] {
    return this.rootStore.authStore.isSalonSession ? this.professionalOptions : this.colleagueOptions
  }

  @computed get calendarTeamMembers(): MemberPickerMember[] {
    const options = this.viewableCalendarOptions
    if (this.rootStore.authStore.isSalonSession) return options

    const ownId = this.rootStore.userStore.data._id
    const colleagues = options.filter(member => member.id !== ownId)
    if (colleagues.length === 0) return []

    const own = options.find(member => member.id === ownId)
    return [{ id: ownId, color: '', ...own, name: this.rootStore.i18n.s('Me') }, ...colleagues]
  }

  agendaMemberId(memberId: string): string | undefined {
    const viewingOwnCalendar = !this.rootStore.authStore.isSalonSession && memberId === this.rootStore.userStore.data._id
    return viewingOwnCalendar ? undefined : memberId
  }

  async loadTeamDay_api(day: string): Promise<boolean> {
    const { agendaStore } = this.rootStore

    const results = await Promise.allSettled(this.calendarTeamMembers.map(member =>
      agendaStore.fetchAgendas_api([day], this.agendaMemberId(member.id), member.name),
    ))

    return results.every(result => result.status === 'fulfilled')
  }

  teamDaySummary(day: string): TeamDayRow[] {
    const { agendaStore, i18n: { s, pluralise } } = this.rootStore

    return this.calendarTeamMembers.map(member => {
      const agendaMemberId = this.agendaMemberId(member.id)
      const loaded = agendaStore.hasAgendaForDay(day, agendaMemberId)
      const bookings = agendaStore.bookingCountOnDay(day, agendaMemberId)
      const available = agendaStore.isAvailableOnDay(day, agendaMemberId)

      const statusText = bookings > 0
        ? s('$0 $1 today', bookings, pluralise('booking', bookings))
        : available ? s('No bookings today') : s('Not available today')

      return { member, loaded, bookings, available, working: available || bookings > 0, statusText }
    })
  }

  memberDisplayName(memberId: string): string | undefined {
    return this.findMember(memberId)?.displayName
      ?? this.colleagues.find(c => c.memberId === memberId)?.displayName
  }

  @action.bound async loadSalon_api(): Promise<boolean> {
    const { authStore } = this.rootStore

    if (!authStore.isSalonSession) {
      runInAction(() => {
        this.salon = null
        this.error = null
      })
      return true
    }

    this.error = null

    try {
      const { salon } = await this.rootStore.communication.requester.getSalon()
      runInAction(() => {
        this.salon = salon ?? null
      })
      return !!salon
    } catch (e) {
      runInAction(() => {
        if (!this.salon) this.error = e instanceof Error ? e.message : String(e)
      })
      return false
    }
  }

  // Replaces a member's permission set. OWNER can be neither granted nor revoked here —
  // the API rejects it — so callers should only pass the editable permissions.
  @action.bound async updateMemberPermissions_api(
    salonMemberId: string,
    permissions: SalonMemberPermission[],
  ): Promise<boolean> {
    try {
      const { updateSalonMemberPermissions } = await this.rootStore.communication.requester.updateSalonMemberPermissions({
        salonMemberId,
        permissions,
      })

      runInAction(() => {
        if (!this.salon) return
        this.salon.members = this.salon.members.map(m =>
          m._id === salonMemberId ? { ...m, ...updateSalonMemberPermissions } : m,
        )
      })

      return true
    } catch (e) {
      return false
    }
  }

  @action.bound async updateSalonMember_api(
    salonMemberId: string,
    data: {
      color?: string
      displayName?: string
      picture?: string | null
      title?: string | null
      completedOnboardingSteps?: SalonMemberOnboardingSteps
    },
  ): Promise<boolean> {
    try {
      const { updateSalonMember } = await this.rootStore.communication.requester.updateSalonMember({
        salonMemberId,
        ...data,
      })

      runInAction(() => {
        if (!this.salon) return
        this.salon.members = this.salon.members.map(m =>
          m._id === salonMemberId ? { ...m, ...updateSalonMember } : m,
        )
      })

      return true
    } catch (e) {
      return false
    }
  }

  @action.bound async completeOnboardingStep_api(
    salonMemberId: string,
    step: SalonMemberOnboardingStep,
  ): Promise<boolean> {
    const member = this.members.find(m => m._id === salonMemberId)

    if (!member || member.completedOnboardingSteps.includes(step)) return false

    return this.updateSalonMember_api(salonMemberId, {
      completedOnboardingSteps: [...member.completedOnboardingSteps, step],
    })
  }

  onboardingStepsWith(salonMemberId: string, step: SalonMemberOnboardingStep): SalonMemberOnboardingSteps {
    const member = this.members.find(m => m._id === salonMemberId)

    return [...new Set([...(member?.completedOnboardingSteps ?? []), step])]
  }

  @action.bound async createReceptionistAccount_api(name: string, email: string): Promise<string | null> {
    try {
      const { createSalonReceptionistAccount } =
        await this.rootStore.communication.requester.createSalonReceptionistAccount({
          name,
          email,
          color: randomMemberColour(this.members.map(member => member.color)),
        })
      await this.loadSalon_api()

      return createSalonReceptionistAccount._id
    } catch (e: any) {
      this.rootStore.errorHandler.showErrorInUI(e)
      return null
    }
  }

  @action.bound async updateReceptionistAccount_api(
    salonMemberId: string,
    data: { name?: string, email?: string },
  ): Promise<boolean> {
    try {
      await this.rootStore.communication.requester.updateSalonReceptionistAccount({ salonMemberId, ...data })
      await this.loadSalon_api()

      return true
    } catch (e: any) {
      this.rootStore.errorHandler.showErrorInUI(e)
      return false
    }
  }

  @action.bound async inviteMember_api(name: string, phoneNumber: string, addSeat: boolean): Promise<string | null> {
    try {
      const { inviteMember } = await this.rootStore.communication.requester.inviteMember({
        name,
        phoneNumber,
        addSeat,
        color: randomMemberColour(this.members.map(member => member.color)),
      })
      await this.loadSalon_api()
      this.rootStore.setupStore.markStepCompleted(GettingStartedStepIds.inviteTeam)

      return inviteMember.memberId
    } catch (e: any) {
      this.rootStore.errorHandler.showErrorInUI(e)
      return null
    }
  }

  @action.bound async dev_setSalonSeats_api(seats: number): Promise<boolean> {
    try {
      const { dev_setSalonSeats } = await this.rootStore.communication.requester.dev_setSalonSeats({ seats })
      return dev_setSalonSeats
    } catch (e: any) {
      this.rootStore.errorHandler.showErrorInUI(e)
      return false
    }
  }

  @action.bound async sendReceptionistMagicLink_api(receptionistId: string): Promise<boolean> {
    try {
      const { sendReceptionistMagicLink } = await this.rootStore.communication.requester.sendReceptionistMagicLink({
        receptionistId,
      })

      return sendReceptionistMagicLink
    } catch (e) {
      return false
    }
  }

  @action.bound async archiveMember_api(
    salonMemberId: string,
    { syncLocal = true }: { syncLocal?: boolean } = {},
  ): Promise<ArchiveMemberResult> {
    try {
      const { archiveSalonMember } = await this.rootStore.communication.requester.archiveSalonMember({
        salonMemberId,
      })

      if (!archiveSalonMember) return ArchiveMemberResult.Failed

      if (syncLocal) {
        this.removeMemberLocally(salonMemberId)
      }

      return ArchiveMemberResult.Archived
    } catch (e) {
      return requestErrorCode(e) === SALON_MEMBER_HAS_UPCOMING_BOOKINGS
        ? ArchiveMemberResult.UpcomingBookings
        : ArchiveMemberResult.Failed
    }
  }

  @action.bound async cancelInvite_api(
    salonMemberId: string,
    { syncLocal = true }: { syncLocal?: boolean } = {},
  ): Promise<boolean> {
    try {
      const { cancelMemberInvite } = await this.rootStore.communication.requester.cancelMemberInvite({
        salonMemberId,
      })

      if (!cancelMemberInvite) return false

      if (syncLocal) {
        this.removeMemberLocally(salonMemberId)
      }

      return true
    } catch (e) {
      return false
    }
  }

  @action.bound async resendInvite_api(salonMemberId: string): Promise<boolean> {
    try {
      const { resendMemberInvite } = await this.rootStore.communication.requester.resendMemberInvite({
        salonMemberId,
      })

      runInAction(() => {
        if (!this.salon) return
        this.salon.members = this.salon.members.map(m =>
          m._id === salonMemberId ? { ...m, ...resendMemberInvite } : m,
        )
      })

      return true
    } catch (e: any) {
      this.rootStore.errorHandler.showErrorInUI(e)
      return false
    }
  }

  @action.bound removeMemberLocally(salonMemberId: string) {
    if (!this.salon) return
    this.salon.members = this.salon.members.filter(m => m._id !== salonMemberId)
  }

  @action.bound async restoreMember_api(salonMemberId: string): Promise<boolean> {
    try {
      const { restoreSalonMember } = await this.rootStore.communication.requester.restoreSalonMember({
        salonMemberId,
      })

      if (!restoreSalonMember) return false

      const previousSalon = this.salon
      const loaded = await this.loadSalon_api()

      if (!loaded) {
        runInAction(() => {
          this.salon = previousSalon
          this.error = null
        })
        return false
      }

      runInAction(() => {
        this.archivedMembers = this.archivedMembers.filter(m => m._id !== salonMemberId)
      })

      return true
    } catch (e) {
      return false
    }
  }

  @observable accessor archivedMembers: ArchivedSalonMember[] = []

  @action.bound async loadArchivedMembers_api(): Promise<void> {
    try {
      const { archivedSalonMembers } = await this.rootStore.communication.requester.archivedSalonMembers()
      runInAction(() => {
        this.archivedMembers = archivedSalonMembers ?? []
      })
    } catch (e) {
      runInAction(() => {
        this.archivedMembers = []
      })
    }
  }

  @computed get membershipsKnown(): boolean {
    return this.membershipsLoaded || this.rootStore.authStore.isReceptionSession
  }

  @action.bound async loadMySalonMembershipsIfNeeded_api(): Promise<void> {
    if (this.membershipsKnown) return

    await this.loadMySalonMemberships_api()
  }

  @action.bound async loadMySalonMemberships_api(): Promise<void> {
    try {
      const { mySalonMemberships } = await this.rootStore.communication.requester.mySalonMemberships()
      runInAction(() => {
        this.mySalonMemberships = mySalonMemberships ?? []
      })
    } catch (e) {
      runInAction(() => {
        this.mySalonMemberships = []
      })
    } finally {
      runInAction(() => {
        this.membershipsLoaded = true
      })
    }
  }

  @action.bound async joinSalon_api(inviteCode: string): Promise<SalonMembership | null> {
    const { communication: { requester }, errorHandler } = this.rootStore

    try {
      const { joinSalon } = await requester.joinSalon({ inviteCode })

      runInAction(() => {
        this.mySalonMemberships = [
          ...this.mySalonMemberships.filter(existing => existing._id !== joinSalon._id),
          joinSalon,
        ]
      })
      return joinSalon
    } catch (e: any) {
      errorHandler.showErrorInUI(e)
      return null
    }
  }

  @action.bound setAddedWorkplace(addedWorkplace: AddedWorkplace | null): void {
    this.addedWorkplace = addedWorkplace
  }

  @action.bound stopListeningOnSalonMember(): void {
    this.salonMemberSubscription?.unsubscribe()
    this.salonMemberSubscription = undefined
  }

  // The server re-reads permissions from Mongo on every request, so this doesn't gate anything —
  // it only stops the cached copy, and the UI it drives (checkout and payments, client details),
  // from lagging behind an admin's change until the next refresh.
  @action.bound async listenOnSalonMemberUpdated_api(): Promise<void> {
    const { communication: { client } } = this.rootStore
    const { applyMembershipUpdate } = this

    // The socket is bound to whoever was signed in when it opened, so an account switch must drop
    // it — otherwise the previous account's memberships land in this store.
    this.stopListeningOnSalonMember()

    this.salonMemberSubscription = client.subscribe<{ salonMemberUpdated?: SalonMembershipUpdate }>({
      query: listeners.salonMemberUpdated,
    }).subscribe({
      next(res) {
        const membership = res.data?.salonMemberUpdated
        if (membership) applyMembershipUpdate(membership)
      },
      // Deliberately quiet: a dropped permissions socket degrades to the old refresh-to-see
      // behaviour, which isn't worth interrupting the user for.
      error(err: any) {
        console.error(err)
      },
    })
  }

  @action.bound applyMembershipUpdate(update: SalonMembershipUpdate): void {
    if (update.status !== SalonMemberStatus.Active) {
      this.mySalonMemberships = this.mySalonMemberships.filter(existing => existing._id !== update._id)
      return
    }

    const known = this.mySalonMemberships.find(existing => existing._id === update._id)

    // The push doesn't carry colleagues, so gaining calendar access needs the list from the query.
    const gainedCalendars = !!known
      && update.permissions.includes(SalonMemberPermission.ViewOtherMemberCalendars)
      && !known.permissions.includes(SalonMemberPermission.ViewOtherMemberCalendars)

    // A membership we've never seen has no businessName or locations to merge into, and the payload
    // doesn't carry them — so reload the list rather than store a half-built row.
    if (!known || gainedCalendars) {
      this.loadMySalonMemberships_api()
      return
    }

    // Merged, not replaced: the payload is membership fields only.
    this.mySalonMemberships = this.mySalonMemberships.map(existing =>
      existing._id === update._id ? { ...existing, ...update } : existing,
    )
  }

  @computed get bookableSalonMemberships(): SalonMembership[] {
    return this.mySalonMemberships
      .filter(m => m.permissions.includes(SalonMemberPermission.CreateAndUpdateBookings))
  }

  hasPermission(ownerId: string, permission: SalonMemberPermission): boolean {
    // Is owner
    if (!ownerId || ownerId === this.rootStore.userStore.data._id) return true

    // Is member of the salon with permission
    return this.mySalonMemberships.some(m =>
      m.salonId === ownerId && m.permissions.includes(permission))
  }

  onlinePaymentsEnabled(ownerId: string | null | undefined): boolean {
    const { userStore } = this.rootStore
    if (!ownerId || ownerId === userStore.data._id) return userStore.onlinePaymentsEnabled

    return !!this.mySalonMemberships.find(m => m.salonId === ownerId)?.onlinePaymentsEnabled
  }

  // The person behind the session. In an assumed salon session their own record was never loaded
  // (userStore.data is the salon), so there is no name to show until adminUserId is exposed.
  @computed get personalAccountLabel(): string {
    const { s } = this.rootStore.i18n
    const { authStore, userStore } = this.rootStore

    // An assumed salon session never loaded the person's own record, so their name comes from the
    // snapshot taken on the way in. Used for sweitch back to user button
    if (authStore.isSalonSession) return authStore.assumedFromName || s('Personal account')

    const { firstName, lastName } = userStore.data
    return `${firstName ?? ''} ${lastName ?? ''}`.trim() || s('Personal account')
  }

  @computed get personalAccountSubtitles(): string[] {
    const { s } = this.rootStore.i18n
    const { authStore, userStore } = this.rootStore

    const lines = this.mySalonMemberships.flatMap(m => {
      if (m.permissions.includes(SalonMemberPermission.Owner)) return []
      if (m.permissions.includes(SalonMemberPermission.Admin)) return []
      return [s('Team member at $0', m.businessName)]
    })

    const ownBusiness = !authStore.isSalonSession && !userStore.salonMemberOnly
      ? userStore.data.professionalProfile.saasSettings.businessName
      : ''

    if (ownBusiness) lines.push(s('Owner of $0', ownBusiness))

    return lines.length > 0 ? lines : [s('Your own Solo account')]
  }

  @computed get accountOptions(): AccountOption[] {
    const { s } = this.rootStore.i18n
    const { activeSalonId, isReceptionSession } = this.rootStore.authStore

    if (isReceptionSession) return []

    const salons: AccountOption[] = this.mySalonMemberships
      .filter(m => m.permissions.includes(SalonMemberPermission.Owner) || m.permissions.includes(SalonMemberPermission.Admin))
      .map(m => ({
        id: m.salonId,
        salonId: m.salonId,
        label: m.businessName,
        subtitles: [m.permissions.includes(SalonMemberPermission.Owner)
          ? s('Salon owner account')
          : s('Admin account')],
        isActive: m.salonId === activeSalonId,
      }))

    if (activeSalonId && !salons.some(o => o.isActive)) {
      salons.unshift({
        id: activeSalonId,
        salonId: activeSalonId,
        label: this.membershipsLoaded ? s('Salon') : '',
        subtitles: this.membershipsLoaded ? [s('Salon account')] : [],
        isActive: true,
        nameLoading: !this.membershipsLoaded,
      })
    }

    return [
      {
        id: PERSONAL_OPTION_ID,
        salonId: null,
        label: this.personalAccountLabel,
        subtitles: this.personalAccountSubtitles,
        isActive: !activeSalonId,
      },
      ...salons,
    ]
  }

  @computed get canSwitchAccount(): boolean {
    return this.accountOptions.length > 1
  }

  @computed get ownedSalonId(): string | null {
    return this.mySalonMemberships.find(isSalonOwner)?.salonId ?? null
  }

  @action.bound async enterOwnedSalon_api(): Promise<void> {
    if (!this.rootStore.authStore.isReceptionSession) {
      await this.loadMySalonMemberships_api()

      const salonId = this.ownedSalonId

      if (salonId && await this.switchAccount_api(salonId)) return
    }

    await this.rootStore.afterAuthenticated()
  }

  // Switching to the account that's already active would needlessly drop and refetch its data.
  @action.bound async switchAccount_api(salonId: string | null): Promise<boolean> {
    const { authStore } = this.rootStore
    if (salonId === authStore.activeSalonId) return true

    return salonId
      ? authStore.signInAsSalon_api(salonId)
      : authStore.signOutOfSalon_api()
  }

  @action.bound clearData(): void {
    this.stopListeningOnSalonMember()
    this.salon = null
    this.mySalonMemberships = []
    this.membershipsLoaded = false
    this.addedWorkplace = null
    this.archivedMembers = []
    this.error = null
  }
}
